import isEmpty from '@/helpers/isEmpty'
import Vue from 'vue'
import { LinkForm } from './containers'
import {
  ELinkType,
  ETarget,
  ILinkFormProps,
  TLinkFormData,
} from './containers/LinkForm/types'
import {
  applyAutoLinkAttributes,
  getFormData,
  getRoutePlatformOrigin,
} from './helpers'
import { Dialog, DialogInstance } from './ui'

const applyLink = (
  editor: CKEDITOR.editor,
  formData: TLinkFormData,
  existingLinkElement: CKEDITOR.dom.element | null,
  savedRange: CKEDITOR.dom.range
) => {
  editor.focus()

  let href = ''
  let rel: string | null = null
  let target: string | null = null

  switch (formData.linkType) {
    case ELinkType.LINK:
      {
        href = formData.href
        rel = formData.rel
        target = formData.target === ETarget.NOT_SET ? null : formData.target
      }

      break

    case ELinkType.LINK_ANCHOR:
      {
        href = `#${formData.anchorId}`
      }

      break

    case ELinkType.EMAIL:
      {
        const url = new URL(`mailto:${formData.email}`)

        url.searchParams.append('subject', formData.subject)
        url.searchParams.append('body', formData.body)

        href = url.toString()
      }

      break

    case ELinkType.PHONE:
      {
        href = `tel:${formData.phoneNumber}`
      }

      break

    default:
      break
  }

  let hasChanges = true

  if (existingLinkElement) {
    hasChanges = [
      {
        oldValue: existingLinkElement.getAttribute('href'),
        newValue: href,
      },
      {
        oldValue: existingLinkElement.getAttribute('rel'),
        newValue: rel,
      },
      {
        oldValue: existingLinkElement.getAttribute('target'),
        newValue: target,
      },
      {
        oldValue: existingLinkElement.getText(),
        newValue: formData.text,
      },
    ].some(({ oldValue, newValue }) => oldValue !== newValue)
  }

  if (!hasChanges) {
    return
  }

  editor.fire('saveSnapshot')

  if (existingLinkElement) {
    existingLinkElement.setAttribute('href', href)
    existingLinkElement.setAttribute('data-cke-saved-href', href)

    if (target) {
      existingLinkElement.setAttribute('target', target)
    } else {
      existingLinkElement.removeAttribute('target')
    }

    if (rel) {
      existingLinkElement.setAttribute('rel', rel)
    } else {
      existingLinkElement.removeAttribute('rel')
    }

    if (formData.text && existingLinkElement.getText() !== formData.text) {
      existingLinkElement.setText(formData.text)
    }

    const rangeAfterEdit = editor.createRange()
    rangeAfterEdit.moveToPosition(
      existingLinkElement,
      CKEDITOR.POSITION_AFTER_END
    )
    rangeAfterEdit.select()
  } else {
    savedRange.select()

    const isCollapsed = savedRange.collapsed

    if (isCollapsed) {
      const linkText = formData.text
      const textNode = new CKEDITOR.dom.text(linkText, editor.document)
      savedRange.insertNode(textNode)
      savedRange.setStartBefore(textNode)
      savedRange.setEndAfter(textNode)
    } else if (formData.text) {
      savedRange.deleteContents()
      const textNode = new CKEDITOR.dom.text(formData.text, editor.document)
      savedRange.insertNode(textNode)
      savedRange.setStartBefore(textNode)
      savedRange.setEndAfter(textNode)
    }

    const attributes: Record<string, string> = {
      href,
      'data-cke-saved-href': href,
    }

    if (target) {
      attributes.target = target
    }

    if (rel) {
      attributes.rel = rel
    }

    const style = new CKEDITOR.style(
      {
        type: CKEDITOR.STYLE_INLINE,
        element: 'a',
        attributes,
      },
      undefined
    )

    style.applyToRange(savedRange, editor)
    savedRange.select()
  }

  editor.fire('saveSnapshot')
  editor.fire('change')
}

export const initTextLinkPlugin = (
  options: Pick<
    ILinkFormProps,
    'checkAllowedIndexingSettings' | 'checkDisabledIndexingSettings'
  >
) => {
  const { checkAllowedIndexingSettings, checkDisabledIndexingSettings } =
    options

  CKEDITOR.on('instanceReady', async ({ editor }) => {
    let isDestroyed = false
    let dialogInstance: Vue | null = null

    editor.on('destroy', () => {
      isDestroyed = true
      dialogInstance?.$destroy()
      dialogInstance?.$el.remove()
    })

    const { getApp } = await import('@/main')
    const app = await getApp

    if (isDestroyed) {
      return
    }

    const openLinkEditor = () => {
      const selectedLinks = CKEDITOR.plugins.link.getSelectedLink(editor)
      const selection = editor.getSelection()

      const selectedElement = Array.isArray(selectedLinks)
        ? selectedLinks[0]
        : selectedLinks

      const range = selection.getRanges()[0]

      let linkEl: CKEDITOR.dom.element | null = null

      if (selectedElement && selectedElement.is('a')) {
        linkEl = selectedElement
      }

      const appContainerEl = document.getElementById('app') || document.body

      const dialogContainerEl = document.createElement('div')

      appContainerEl.appendChild(dialogContainerEl)

      const formData = getFormData(linkEl)

      if (!formData.text) {
        formData.text = selection.getSelectedText()
      }

      dialogInstance = new Vue({
        vuetify: app.$options.vuetify,
        store: app.$options.store,
        router: app.$options.router,
        components: {
          Dialog,
          LinkForm,
        },
        data() {
          return {
            formData,
          }
        },
        el: dialogContainerEl,
        methods: {
          getDialogRef() {
            return this.$refs.dialog as DialogInstance | undefined
          },
        },
        render(h) {
          return h(
            Dialog,
            {
              ref: 'dialog',
              props: {
                value: true,
              },
              attrs: {
                contentClass: 'white pt-5 px-5 rounded-0',
                maxWidth: '574',
                tile: true,
              },
              on: {
                hidden: () => {
                  this.$destroy()
                  this.$el.remove()
                },
              },
            },
            [
              h(LinkForm, {
                props: {
                  data: this.formData,
                  checkAllowedIndexingSettings,
                  checkDisabledIndexingSettings,
                },
                attrs: {
                  class: 'pb-5',
                },
                slot: 'default',
                on: {
                  cancel: () => {
                    this.getDialogRef()?.toggleDialog(false)
                  },
                  submit: (data: TLinkFormData) => {
                    applyLink(editor, data, linkEl, range)
                    this.getDialogRef()?.toggleDialog(false)
                  },
                },
              }),
            ]
          )
        },
      })

      return false
    }

    const originalOpenDialog = editor.openDialog

    // @ts-ignore
    editor.openDialog = function (...args) {
      const [dialogName] = args

      if (dialogName !== 'link') {
        return originalOpenDialog.call(editor, ...args)
      }

      openLinkEditor()

      return null
    }

    editor.getCommand('link').exec = openLinkEditor

    editor.on(
      'paste',
      (eventInfo) => {
        if (
          typeof eventInfo.data.dataValue !== 'string' ||
          isEmpty(eventInfo.data.dataValue)
        ) {
          return
        }

        eventInfo.data.dataValue = applyAutoLinkAttributes({
          html: eventInfo.data.dataValue,
          platformOrigin: getRoutePlatformOrigin(app.$route.path),
        })
      },
      null,
      null,
      999
    )
  })
}
